import {
  BIRDModelController,
  AutoInteractionController,
  Section,
  AutoInteractionMode as BirdAutoInteractionMode,
} from '@sas-dvr/ltjs-bird';
import { merge, Observable, EMPTY, of } from 'rxjs';
import { fromPortableEvent, withRetained } from '../util';
import { tap, map } from 'rxjs/operators';
import { ofActionCreatorType } from '../util/rxUtils';
import {
  AutoInteractionState,
  AutoInteractionMode,
} from '@sas-dvr/internal-va-react-core/store/autoInteraction/AutoInteractionState';
import { AnyAction } from '@sas/nova-commons/action';

export const loadAutoInteractionController = (
  controller: AutoInteractionController | null,
  section: Section,
  myActions$: Observable<AnyAction>,
  _unload$: Observable<BIRDModelController>,
  id: string
): Observable<AnyAction> => {
  if (!controller) {
    return EMPTY;
  }
  const getNextUpdate = () => makeSetStateAction(id, controller, section);

  const initialAction = getNextUpdate();

  const handleExpressionUpdated$ = fromPortableEvent(
    controller,
    AutoInteractionController.EXPRESSION_UPDATED
  ).pipe(map(getNextUpdate));

  const handleDeleteItem$ = myActions$.pipe(
    ofActionCreatorType<typeof AutoInteractionState.actions.deleteItem>(
      AutoInteractionState.actionTypes.deleteItem
    ),
    tap((action) => {
      const itemIndex = action.payload.data.itemIndex;
      const items = controller.getExpressionControlItems();
      const item = items && items[itemIndex];
      if (!item) {
        console.error('Expected group at index ' + itemIndex);
        return;
      }
      item.removeAllValues();
    }),
    map(getNextUpdate)
  );

  const handleDeleteValue$ = myActions$.pipe(
    ofActionCreatorType<typeof AutoInteractionState.actions.deleteValue>(
      AutoInteractionState.actionTypes.deleteValue
    ),
    tap((action) => {
      const { itemIndex: group, valueIndex: value } = action.payload.data;
      // TODO: ExpressionControlItem is deprecated, use SelectionControl
      const items = controller.getExpressionControlItems();
      const item = items && items[group];
      if (!item) {
        console.error('Expected group at index ' + value);
        return;
      }
      if (item.getValueList()?.length === 1) {
        item.removeAllValues();
      } else {
        item.removeValueFromList(value);
      }
    }),
    map(getNextUpdate)
  );

  return merge(
    of(initialAction),
    handleExpressionUpdated$,
    handleDeleteItem$,
    handleDeleteValue$
  ).pipe(withRetained(controller), withRetained(section));
};

function makeSetStateAction(
  id: string,
  controller: AutoInteractionController,
  section: Section
): AnyAction {
  const controlItems = controller.getExpressionControlItems() || [];

  return AutoInteractionState.actions.set(id, {
    showControl: section.getShowSelectionControl(),
    mode: convertAutoInteractionMode(controller.getAutoInteractionMode()),
    items: controlItems.map((item) => {
      if (!item) {
        return { label: '', readonly: true, values: [] };
      }
      const values = item.getValueList() || [];
      return {
        label: item.getLabel() || '',
        values: values.map((value) => value || ''),
        readonly: item.readOnly(),
      };
    }),
  });
}

function convertAutoInteractionMode(
  autoInteractionMode: BirdAutoInteractionMode
): AutoInteractionMode {
  switch (autoInteractionMode) {
    case BirdAutoInteractionMode.BRUSH:
      return 'brush';
    case BirdAutoInteractionMode.ONEWAYFILTER:
      return 'onewayfilter';
    case BirdAutoInteractionMode.TWOWAYFILTER:
      return 'twowayfilter';
    case BirdAutoInteractionMode.NONE:
    default:
      return 'none';
  }
}
